import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'

import { Button } from '@/components/ui/button'
import { getStockStatus } from '@/lib/format'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import { toCatalogProduct, useInventoryActions } from '@/components/inventory/inventory-actions'
import StockStatusBadge from '@/components/inventory/stock-status-badge'
import StockMeter from '@/components/inventory/stock-meter'
import UnitLabel from '@/components/inventory/unit-label'

interface RestockListProps {
    products: Product[]
}

const EntryIcon = MOVEMENT_ICONS.entrada

const RestockList: FC<RestockListProps> = ({ products }): ReactNode => {
    const { number } = useSettings()
    const { openMovement } = useInventoryActions()

    return (
        <ul className='-my-3 divide-y'>
            {products.map(product => {
                const status = getStockStatus(product.quantity, product.minStock)
                const missing = Math.max(product.minStock - product.quantity, 0)

                return (
                    <li key={product.id} className='flex items-center gap-3 py-3 sm:gap-4'>
                        <div className='min-w-0 flex-1'>
                            <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                                <Link
                                    href={`/inventario/${product.id}`}
                                    className='min-w-0 truncate text-sm font-medium text-foreground hover:text-primary hover:underline underline-offset-4'
                                >
                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                </Link>

                                <StockStatusBadge status={status} className='h-5 px-1.5 text-[0.7rem] sm:hidden' />
                            </div>

                            <div className='mt-2 flex items-center gap-3'>
                                <StockMeter quantity={product.quantity} minStock={product.minStock} className='max-w-36' />

                                <span className='shrink-0 text-xs text-muted-foreground tabular-nums'>
                                    <span className='font-semibold text-foreground'>{number(product.quantity)}</span>
                                    {' de '}
                                    <span>{number(product.minStock)}</span> <UnitLabel productId={product.id} unit={product.unit} />
                                </span>
                            </div>
                        </div>

                        <div className='hidden flex-col items-end gap-1 sm:flex'>
                            <StockStatusBadge status={status} />

                            {missing > 0 && (
                                <span className='text-xs text-muted-foreground tabular-nums'>
                                    Faltan {number(missing)}
                                </span>
                            )}
                        </div>

                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => openMovement({ product: toCatalogProduct(product), type: 'entrada' })}
                            aria-label={`Registrar entrada de ${product.name}`}
                        >
                            <EntryIcon className='text-flow-in-ink' aria-hidden='true' />
                            <span className='hidden sm:inline'>Reponer</span>
                        </Button>
                    </li>
                )
            })}
        </ul>
    )
}

export default RestockList
